/**
 * Health_Cache facade: the single entry point the app uses to read and update
 * a customer's cached health payload. It composes the lower-level
 * `recordsCache`, `snapshotCache`, and `freshness` modules and owns the
 * persisted Records_Cache shape (`{ url, lastFetchTime, records }`).
 *
 * The Records_Cache and Snapshot_Cache live under separate `localStorage` keys
 * so a non-page-1 fetch can update records without touching the snapshot
 * (Req 8.3). Freshness is driven by Last_Fetch_Time, which — together with the
 * snapshot — is only updated on a page-1 fetch so that paginating older records
 * never makes the cache look freshly fetched (Req 7.4, 7.5, 8.3, 8.4).
 *
 * Every operation derives its keys from the customer id, so reading or writing
 * one customer's entry never touches another's (Req 14.1, 14.2, 14.3).
 *
 * Satisfies:
 * - Requirement 1.1: cached health data is keyed and retrievable per customer.
 * - Requirement 1.2: a Cache_Entry carries records, snapshot, request URL, and
 *   Last_Fetch_Time.
 * - Requirement 1.3/1.4: `loadCached` returns null on a cache miss / parse
 *   failure (delegated to the throw-safe storage layer).
 * - Requirement 7.4/7.5: Last_Fetch_Time is updated to Now only on a page-1
 *   merge.
 * - Requirement 8.3/8.4: the snapshot is replaced only on a page-1 merge.
 * - Requirement 14.1/14.2/14.3: per-customer isolation for reads and writes.
 */

import { RawHealthRecord, mergeRecords } from './recordsCache';
import { SnapshotPayload, readSnapshot, writeSnapshot } from './snapshotCache';
import { FreshnessTier, computeTier } from './freshness';
import { readJson, writeJson } from './storage';
import { HEALTH_RECORDS_KEY } from './keys';

/**
 * A customer's full cached health payload, assembled from the Records_Cache and
 * Snapshot_Cache (Req 1.2).
 */
export interface CacheEntry {
  /** Records_Cache content: merged, deduped, sorted records list. */
  records: RawHealthRecord[];
  /** Snapshot_Cache content, or `null` when no snapshot is cached. */
  snapshot: SnapshotPayload | null;
  /** The request URL associated with the cached data (Req 1.2). */
  url: string;
  /** Last_Fetch_Time: epoch-ms of the last page-1 fetch (Req 1.2). */
  lastFetchTime: number;
}

/** Options describing the fetched page being merged. */
export interface MergeOptions {
  /** 1-based page number of the fetched page. */
  page: number;
  /** The request URL used to fetch the page. */
  url: string;
}

/**
 * The shape persisted under `HEALTH_RECORDS_KEY`: the records list plus the
 * request URL and Last_Fetch_Time that the facade needs to reconstruct a
 * Cache_Entry.
 */
interface StoredRecordsCache {
  url: string;
  lastFetchTime: number;
  records: RawHealthRecord[];
}

/**
 * Narrow an unknown stored value to a `StoredRecordsCache` (has a `records`
 * array).
 */
function isStoredRecordsCache(value: unknown): value is StoredRecordsCache {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { records?: unknown }).records)
  );
}

/**
 * Read the persisted Records_Cache object for a customer.
 *
 * @returns the stored `{ url, lastFetchTime, records }` object, or `null` on a
 *   cache miss / parse failure (Req 1.3, 1.4).
 */
function readRecordsCache(customerId: string): StoredRecordsCache | null {
  const stored = readJson<unknown>(HEALTH_RECORDS_KEY(customerId));
  return isStoredRecordsCache(stored) ? stored : null;
}

/**
 * Persist the Records_Cache object for a customer. Touches only this customer's
 * records key (Req 14.3); write failures are swallowed by the storage layer.
 */
function writeRecordsCache(customerId: string, value: StoredRecordsCache): void {
  writeJson(HEALTH_RECORDS_KEY(customerId), value);
}

/**
 * Load a customer's full Cache_Entry from storage.
 *
 * @returns the assembled `CacheEntry`, or `null` when there is no cached
 *   records data at all (cache miss / parse failure) (Req 1.3, 1.4).
 */
export function loadCached(customerId: string): CacheEntry | null {
  const recordsCache = readRecordsCache(customerId);
  if (recordsCache === null) {
    // No records entry: report as no cached data (Req 1.3, 1.4).
    return null;
  }

  return {
    records: recordsCache.records,
    snapshot: readSnapshot(customerId),
    url: recordsCache.url,
    lastFetchTime: recordsCache.lastFetchTime,
  };
}

/**
 * Classify the freshness tier of a customer's cache.
 *
 * @returns the `FreshnessTier`, or `null` when there is no cached data / no
 *   Last_Fetch_Time to classify.
 */
export function getTier(customerId: string, now: number): FreshnessTier | null {
  const cached = loadCached(customerId);
  if (cached === null || typeof cached.lastFetchTime !== 'number') {
    return null;
  }
  return computeTier(cached.lastFetchTime, now);
}

/**
 * Merge one fetched page into a customer's cache and persist the result.
 *
 * Records are always merged into the existing list via `mergeRecords`. The
 * Last_Fetch_Time and snapshot are updated ONLY for a page-1 fetch:
 * - `opts.page === 1`: Last_Fetch_Time becomes `now`, the URL becomes
 *   `opts.url`, and the snapshot is replaced wholesale (Req 7.4, 8.3).
 * - `opts.page !== 1`: the merged records are persisted, but the prior
 *   Last_Fetch_Time, URL, and snapshot are left unchanged (Req 7.5, 8.4).
 *
 * All writes touch only this customer's keys (Req 14.3).
 *
 * @returns the resulting `CacheEntry` reflecting the persisted state.
 */
export function mergePage(
  customerId: string,
  page: { records: RawHealthRecord[]; snapshot: SnapshotPayload },
  opts: MergeOptions,
  now: number,
): CacheEntry {
  const existing = readRecordsCache(customerId);
  const mergedRecords = mergeRecords(existing?.records ?? [], page.records);

  if (opts.page === 1) {
    // Page 1: refresh Last_Fetch_Time, URL, and the snapshot wholesale.
    const stored: StoredRecordsCache = {
      url: opts.url,
      lastFetchTime: now,
      records: mergedRecords,
    };
    writeRecordsCache(customerId, stored);
    writeSnapshot(customerId, page.snapshot);

    return {
      records: mergedRecords,
      snapshot: page.snapshot,
      url: opts.url,
      lastFetchTime: now,
    };
  }

  // Non-page-1: persist merged records but leave Last_Fetch_Time, URL, and the
  // snapshot unchanged (Req 7.5, 8.4).
  const priorUrl = existing?.url ?? opts.url;
  const priorLastFetchTime = existing?.lastFetchTime ?? 0;
  const stored: StoredRecordsCache = {
    url: priorUrl,
    lastFetchTime: priorLastFetchTime,
    records: mergedRecords,
  };
  writeRecordsCache(customerId, stored);

  return {
    records: mergedRecords,
    snapshot: readSnapshot(customerId),
    url: priorUrl,
    lastFetchTime: priorLastFetchTime,
  };
}

/* -------------------------------------------------------------------------- *
 * Optimistic updates (Req 9)
 *
 * Each function performs a local mutation against the persisted cache and
 * persists the result immediately (Req 9.5), deriving every key from the
 * customer id so only this customer's entry is touched (Req 14.3). None of
 * them performs a server refetch — they mutate the already-cached payload in
 * place so the UI can reflect the change instantly.
 * -------------------------------------------------------------------------- */

/**
 * Assemble the current `CacheEntry` for a customer from the persisted
 * Records_Cache and Snapshot_Cache. Used to build the return value of the
 * optimistic-update helpers after they persist a mutation.
 */
function currentEntry(customerId: string): CacheEntry {
  const recordsCache = readRecordsCache(customerId);
  return {
    records: recordsCache?.records ?? [],
    snapshot: readSnapshot(customerId),
    url: recordsCache?.url ?? '',
    lastFetchTime: recordsCache?.lastFetchTime ?? 0,
  };
}

/**
 * Load the snapshot to mutate, falling back to an empty snapshot when none is
 * cached yet. This keeps the note/visitation mutations tolerant of a
 * records-only cache (Req 9 optimistic updates must never throw).
 */
function snapshotForMutation(customerId: string): SnapshotPayload {
  return (
    readSnapshot(customerId) ?? {
      profile: null,
      important_notes: [],
      vitals_last_30_days: null,
      resident_visitation: [],
    }
  );
}

/** Coerce an unknown snapshot field to an array of plain objects to mutate. */
function asObjectArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (e): e is Record<string, unknown> => typeof e === 'object' && e !== null,
  );
}

/** Loosely compare a note/visitation id (which may be a number or string). */
function idEquals(a: unknown, b: number): boolean {
  return a === b || (a != null && String(a) === String(b));
}

/**
 * Optimistically add a freshly created health record to the cache (Req 9.1).
 *
 * The new record is merged into the existing list via `mergeRecords` (so it is
 * deduped by Record_Key, sorted, and bounded just like a fetched page), and
 * the Records_Cache is rewritten preserving the existing `url` and
 * `lastFetchTime` — this is a local mutation, not a page-1 fetch, so freshness
 * is intentionally left unchanged.
 *
 * @returns the resulting `CacheEntry` whose `records` contain the new
 *   Record_Key.
 */
export function applyAddRecord(customerId: string, raw: RawHealthRecord): CacheEntry {
  const existing = readRecordsCache(customerId);
  const mergedRecords = mergeRecords(existing?.records ?? [], [raw]);

  const stored: StoredRecordsCache = {
    url: existing?.url ?? '',
    lastFetchTime: existing?.lastFetchTime ?? 0,
    records: mergedRecords,
  };
  writeRecordsCache(customerId, stored);

  return {
    records: mergedRecords,
    snapshot: readSnapshot(customerId),
    url: stored.url,
    lastFetchTime: stored.lastFetchTime,
  };
}

/**
 * Optimistically edit an important note's text in the snapshot (Req 9.2).
 *
 * Finds the note in the snapshot's `important_notes` whose `id` matches and
 * sets its `notes` text field (the text field used by `parseImportantNotes`).
 * Non-matching notes are left untouched. The snapshot is persisted immediately.
 *
 * @returns the resulting `CacheEntry` reflecting the edited note.
 */
export function applyEditNote(customerId: string, id: number, notes: string): CacheEntry {
  const snapshot = snapshotForMutation(customerId);
  const updatedNotes = asObjectArray(snapshot.important_notes).map((note) =>
    idEquals(note.id, id) ? { ...note, notes } : note,
  );

  writeSnapshot(customerId, { ...snapshot, important_notes: updatedNotes });
  return currentEntry(customerId);
}

/**
 * Optimistically unflag (remove) an important note from the snapshot (Req 9.3).
 *
 * Removes the note whose `id` matches from the snapshot's `important_notes`
 * array, so the unflagged note no longer appears as important. The snapshot is
 * persisted immediately.
 *
 * @returns the resulting `CacheEntry` with the note removed from
 *   `important_notes`.
 */
export function applyUnflagNote(customerId: string, id: number): CacheEntry {
  const snapshot = snapshotForMutation(customerId);
  const remaining = asObjectArray(snapshot.important_notes).filter(
    (note) => !idEquals(note.id, id),
  );

  writeSnapshot(customerId, { ...snapshot, important_notes: remaining });
  return currentEntry(customerId);
}

/**
 * Optimistically mark a resident visitation as completed in the snapshot
 * (Req 9.4).
 *
 * Finds the visitation in the snapshot's `resident_visitation` array whose `id`
 * matches and sets its `status` to `'Completed'` (the raw API casing consumed
 * by `parseResidentVisitations`). The snapshot is persisted immediately.
 *
 * @returns the resulting `CacheEntry` reflecting the completed visitation.
 */
export function applyVisitCompleted(customerId: string, id: number): CacheEntry {
  const snapshot = snapshotForMutation(customerId);
  const updatedVisits = asObjectArray(snapshot.resident_visitation).map((visit) =>
    idEquals(visit.id, id) ? { ...visit, status: 'Completed' } : visit,
  );

  writeSnapshot(customerId, { ...snapshot, resident_visitation: updatedVisits });
  return currentEntry(customerId);
}
